/**
 * What the Library does (W2-10 projects-data; DESIGN.md §4.1, §5.13, §5.16; ARCHITECTURE.md §10), shared by the
 * page, the card menus and the `project.*` commands.
 *
 *  - Opening goes through `nav.openProject`; a project another tab edits (PersistError 'lockedElsewhere') opens the
 *    "Open in another tab" choice: *Switch to it* asks that tab to come forward, *Edit here instead* takes the lock
 *    over (`persist.takeOverLock`: the other tab saves, closes the chart and returns to its Library) and opens it.
 *  - Rename uses `persist.renameProject` (a journaled txn, undoable once the chart is open) for a closed chart and
 *    an ordinary transaction for the chart open in this tab.
 *  - Trash keeps a chart 30 days (toast with Restore); "Delete forever" and "Empty Trash" ask first.
 *  - New difficulty shares the song and illustration and copies tempo, offset and song details (start empty) or
 *    the whole chart (copy); the new chart opens with its own level label and number (when that first open fails,
 *    the level is set the next time the chart opens in this session).
 *  - "Download all as backup" flushes, then zips every chart in the io worker (progress from its events).
 * Failures reach the user as toasts with the reason; nothing throws out of these functions.
 */

import { DIALOG } from '../../app/dialogs/types';
import type { AppServices } from '../../app/registry/types';
import { Core } from '../../core/commands/index';
import type { ProjectId } from '../../core/model/types';
import type { MessageRef } from '../../core/util/types';
import type { MessageKey } from '../../i18n/types';
import type { ProjectSummary } from '../../persist/types';
import type { WorkspaceId } from '../../state/types';
import { downloadBlob, extensionFor } from './files';
import { m } from './library.messages';
import { dateStamp, errorMessage, fileNameOf, persistCode } from './libraryModel';
import { libraryStore, liveProjects, type LockedRequest, withBusy } from './libraryStore';
import { requestFocus } from './tabBus';

type Services = AppServices;

function ref(key: MessageKey, params?: Readonly<Record<string, string | number>>): MessageRef {
  return params === undefined ? { key } : { key, params };
}

/** Display name of a project ("Untitled chart" when empty). */
export function displayName(services: Pick<Services, 'i18n'>, p: { readonly name: string }): string {
  return p.name.trim() === '' ? services.i18n.t(m.untitled) : p.name;
}

/** Toast for a failed persistence call (the reason in words). */
function failed(services: Services, err: unknown, name?: string): void {
  const code = persistCode(err);
  if (code === 'lockedElsewhere' && name !== undefined) {
    services.ui.toast(ref(m.inOtherTab, { name }), { kind: 'warning' });
    return;
  }
  if (code === 'notFound') {
    services.ui.toast(ref(m.notFound), { kind: 'error' });
    return;
  }
  if (code === 'quota') {
    services.ui.toast(ref(m.quota), { kind: 'error', durationMs: 10_000 });
    return;
  }
  services.ui.toast(ref(m.actionFailed, { reason: errorMessage(err) }), { kind: 'error' });
}

// ---------------------------------------------------------------------------------------------------------------
// Listing
// ---------------------------------------------------------------------------------------------------------------

/** Reads every project (incl. the Trash) into the Library store. */
export async function refreshProjects(services: Pick<Services, 'persist'>): Promise<readonly ProjectSummary[]> {
  const store = libraryStore(services);
  try {
    const projects = await services.persist.listProjects(true);
    store.update((s) => ({
      projects,
      loadError: null,
      listedAt: Date.now(),
      focused: s.focused !== null && !projects.some((p) => p.id === s.focused) ? null : s.focused,
    }));
    return projects;
  } catch (err) {
    store.update({ loadError: errorMessage(err) });
    return libraryStore(services).get().projects ?? [];
  }
}

/** The project with this id as persistence lists it now (another tab may have changed it since the last listing). */
export async function findProject(services: Services, id: ProjectId): Promise<ProjectSummary | null> {
  const list = await refreshProjects(services);
  return list.find((p) => p.id === id) ?? null;
}

// ---------------------------------------------------------------------------------------------------------------
// Opening and the "Open in another tab" choice
// ---------------------------------------------------------------------------------------------------------------

/**
 * Opens a project in this tab. Resolves true when it is open; on 'lockedElsewhere' the "Open in another tab"
 * choice appears (the Library's dialog, or a system choice outside the Library) and `then` runs once the user
 * chose to edit here and the project opened.
 */
export async function openProject(
  services: Services,
  p: { readonly id: ProjectId; readonly name: string },
  opts: {
    readonly workspace?: WorkspaceId;
    readonly then?: () => Promise<void> | void;
  } = {},
): Promise<boolean> {
  const name = displayName(services, p);
  try {
    await services.nav.openProject(p.id, opts.workspace);
  } catch (err) {
    const code = persistCode(err);
    if (code === 'lockedElsewhere') {
      const request: LockedRequest = {
        id: p.id,
        name,
        ...(opts.workspace === undefined ? {} : { workspace: opts.workspace }),
        ...(opts.then === undefined ? {} : { then: opts.then }),
      };
      const store = libraryStore(services);
      if (store.get().mounted > 0) store.update({ locked: request });
      else void chooseLockedElsewhere(services, request);
      return false;
    }
    if (code === 'notFound') {
      services.ui.toast(ref(m.notFound), { kind: 'error' });
      void refreshProjects(services);
      return false;
    }
    services.ui.toast(ref(m.openFailed, { name, reason: errorMessage(err) }), { kind: 'error' });
    return false;
  }
  if (opts.then !== undefined) await opts.then();
  return true;
}

/** "Switch to it": asks the tab that has the chart to come forward (browsers do not let a page switch tabs). */
export function switchToOtherTab(services: Services, request: LockedRequest): void {
  requestFocus(request.id);
  services.ui.toast(ref(m.leSwitched, { name: request.name }), { kind: 'info', durationMs: 10_000 });
}

/**
 * "Edit here instead": the other tab saves, closes the chart and returns to its Library; then the chart opens here.
 * 'opened': it is open here; 'refused': the other tab kept it (nothing changed); 'error': shown as a toast.
 */
export async function editHereInstead(
  services: Services,
  request: LockedRequest,
): Promise<'opened' | 'refused' | 'error'> {
  let granted: boolean;
  try {
    granted = await services.persist.takeOverLock(request.id);
  } catch (err) {
    failed(services, err, request.name);
    return 'error';
  }
  if (!granted) return 'refused';
  const opened = await openProject(services, request, {
    ...(request.workspace === undefined ? {} : { workspace: request.workspace }),
    ...(request.then === undefined ? {} : { then: request.then }),
  });
  return opened ? 'opened' : 'error';
}

/** The choice as a system dialog (when no Library page is mounted, e.g. a palette command from Settings). */
async function chooseLockedElsewhere(services: Services, request: LockedRequest): Promise<void> {
  const choice = await services.ui.choose<'switch' | 'here'>({
    title: ref(m.leTitle, { name: request.name }),
    body: ref(m.leBody),
    options: [
      { value: 'switch', label: ref(m.leSwitch), description: ref(m.leSwitchHelp) },
      { value: 'here', label: ref(m.leEditHere), description: ref(m.leEditHereHelp) },
    ],
  });
  if (choice === 'switch') {
    switchToOtherTab(services, request);
    return;
  }
  if (choice !== 'here') return;
  if ((await editHereInstead(services, request)) === 'refused') {
    services.ui.toast(ref(m.leFailed), { kind: 'error', durationMs: 10_000 });
  }
}

// ---------------------------------------------------------------------------------------------------------------
// Card actions
// ---------------------------------------------------------------------------------------------------------------

/** Rename (empty names refused). The chart open in this tab is renamed by an undoable transaction. */
export async function renameProject(services: Services, p: ProjectSummary, name: string): Promise<boolean> {
  const next = name.trim();
  if (next === '') {
    services.ui.toast(ref(m.renameEmpty), { kind: 'warning' });
    return false;
  }
  if (next === p.name) return true;
  const store = services.store;
  if (store !== null && store.projectId === p.id) {
    const out = store.transact({ label: ref(m.renameTxn, { name: next }), command: 'project.rename' }, (tx) => {
      Core.doc.patchMeta(tx, { patch: { name: next } });
    });
    if (!out.ok) {
      services.ui.toast(out.reason, { kind: 'error' });
      return false;
    }
    services.ui.toast(ref(m.renamed, { name: next }), { kind: 'success', undo: true });
    return true;
  }
  const lib = libraryStore(services);
  try {
    await withBusy(lib, p.id, () => services.persist.renameProject(p.id, next));
  } catch (err) {
    failed(services, err, displayName(services, p));
    return false;
  }
  services.ui.toast(ref(m.renamed, { name: next }), { kind: 'success' });
  await refreshProjects(services);
  return true;
}

/** Duplicate (everything copied; the copy is named "<name> (copy)"). Resolves with the new id. */
export async function duplicateProject(services: Services, p: ProjectSummary): Promise<ProjectId | null> {
  const name = services.i18n.t(m.copyName, { name: displayName(services, p) });
  const lib = libraryStore(services);
  try {
    await services.persist.flush();
    const id = await withBusy(lib, p.id, () => services.persist.duplicateProject(p.id, { newDifficulty: false, name }));
    await refreshProjects(services);
    lib.update({ focused: id });
    services.ui.toast(ref(m.duplicated, { name }), {
      kind: 'success',
      action: { label: ref(m.open), command: 'project.open', args: { id } },
    });
    return id;
  } catch (err) {
    failed(services, err, displayName(services, p));
    return null;
  }
}

/** Moves a chart to the Trash (kept 30 days) with a Restore action. The chart open in this tab is refused. */
export async function trashProject(services: Services, p: ProjectSummary): Promise<boolean> {
  if (services.store?.projectId === p.id) {
    services.ui.toast(ref(m.trashOpenChart), { kind: 'warning' });
    return false;
  }
  const lib = libraryStore(services);
  const name = displayName(services, p);
  try {
    await withBusy(lib, p.id, () => services.persist.trashProject(p.id));
  } catch (err) {
    failed(services, err, name);
    return false;
  }
  // The Library page keeps the keyboard and its tab stop on the neighbouring card as shown (ProjectGrid).
  services.ui.toast(ref(m.trashed, { name }), {
    kind: 'info',
    durationMs: 8000,
    action: { label: ref(m.restore), command: 'project.restore', args: { id: p.id } },
  });
  await refreshProjects(services);
  return true;
}

export async function restoreProject(services: Services, p: ProjectSummary): Promise<boolean> {
  const lib = libraryStore(services);
  try {
    await withBusy(lib, p.id, () => services.persist.restoreProject(p.id));
  } catch (err) {
    failed(services, err, displayName(services, p));
    return false;
  }
  services.ui.toast(ref(m.restored, { name: displayName(services, p) }), { kind: 'success' });
  await refreshProjects(services);
  return true;
}

/** Deletes charts for good after asking (one chart, or the whole Trash). Resolves with the number deleted. */
export async function deleteForever(services: Services, list: readonly ProjectSummary[]): Promise<number> {
  const first = list[0];
  if (first === undefined) return 0;
  const ok = await services.ui.confirm({
    title:
      list.length === 1
        ? ref(m.deleteOneTitle, { name: displayName(services, first) })
        : ref(m.deleteManyTitle, { count: list.length }),
    body: ref(m.deleteBody),
    confirm: ref(m.deleteConfirm),
    danger: true,
  });
  if (!ok) return 0;
  const lib = libraryStore(services);
  let deleted = 0;
  for (const p of list) {
    try {
      await withBusy(lib, p.id, () => services.persist.deleteForever(p.id));
      deleted++;
    } catch (err) {
      failed(services, err, displayName(services, p));
    }
  }
  if (deleted > 0) services.ui.toast(ref(m.deleted, { count: deleted }), { kind: 'success' });
  await refreshProjects(services);
  return deleted;
}

/** Opens the chart in the Publish workspace and starts the export there. */
export async function exportProject(services: Services, p: ProjectSummary): Promise<void> {
  const name = displayName(services, p);
  if (services.store?.projectId !== p.id) {
    services.ui.toast(ref(m.exportOpening, { name }), { kind: 'info', durationMs: 3000 });
  }
  await openProject(services, p, {
    workspace: 'publish',
    then: async () => {
      if (services.registry.command('file.export') === undefined) return;
      await services.registry.run('file.export', undefined, 'menu');
    },
  });
}

/** The "Version history" dialog of a chart (open or not). */
export async function showVersions(services: Services, p: { readonly id: ProjectId }): Promise<void> {
  await services.ui.openDialog(DIALOG.versions, { projectId: p.id });
}

/** Downloads the raw snapshot and journal of a chart (for bug reports when its edits could not be replayed). */
export async function downloadRecoveryData(services: Services, p: ProjectSummary): Promise<void> {
  const name = displayName(services, p);
  try {
    const blob = await services.persist.recoveryData(p.id);
    const base = services.i18n.t(m.recoveryFileName, { name: fileNameOf(name, p.level), date: dateStamp() });
    downloadBlob(blob, `${fileNameOf(base, '')}.${extensionFor(blob.type)}`);
    services.ui.toast(ref(m.recoverySaved, { name }), { kind: 'success' });
  } catch (err) {
    failed(services, err, name);
  }
}

/** Asks the browser to keep Phiedit's storage (persistent storage); resolves with the answer. */
export async function protectCharts(services: Services): Promise<boolean> {
  const granted = await services.persist.requestPersistence().catch(() => false);
  services.ui.toast(ref(granted ? m.protectGranted : m.protectDenied), {
    kind: granted ? 'success' : 'warning',
    durationMs: 8000,
  });
  return granted;
}

/** "Download all as backup": every live chart as .pez + phiedit.json in one zip, made by the io worker. */
export async function backupAll(services: Services): Promise<boolean> {
  const projects = liveProjects(libraryStore(services).get().projects);
  const list = projects.length > 0 ? projects : (await refreshProjects(services)).filter((p) => p.trashedAt === null);
  if (list.length === 0) {
    services.ui.toast(ref(m.backupNothing), { kind: 'info' });
    return false;
  }
  try {
    await services.persist.flush();
    const blob = await services.io.call('backupAll', { projectIds: list.map((p) => p.id) });
    downloadBlob(blob, `${fileNameOf(services.i18n.t(m.backupFileName, { date: dateStamp() }), '')}.zip`);
    services.ui.toast(ref(m.backupDone, { count: list.length }), { kind: 'success' });
    return true;
  } catch (err) {
    services.ui.toast(ref(m.backupFailed, { reason: errorMessage(err) }), { kind: 'error', durationMs: 10_000 });
    return false;
  }
}

// ---------------------------------------------------------------------------------------------------------------
// New difficulty
// ---------------------------------------------------------------------------------------------------------------

export interface NewDifficultyChoice {
  /** 'empty': song settings only; 'copy': the whole chart. */
  readonly start: 'empty' | 'copy';
  /** Display level, e.g. "HD Lv.11". */
  readonly level: string;
  readonly difficulty: number;
}

/**
 * Creates a new difficulty of `source` and opens it with its level. Resolves with the new id (null on failure).
 */
export async function createDifficulty(
  services: Services,
  source: ProjectSummary,
  choice: NewDifficultyChoice,
): Promise<ProjectId | null> {
  const name = displayName(services, source);
  const lib = libraryStore(services);
  let id: ProjectId;
  try {
    await services.persist.flush();
    id = await withBusy(lib, source.id, () =>
      // The copy carries its level from the start (PersistClient.duplicateProject, CR-W2-10-2).
      services.persist.duplicateProject(source.id, {
        newDifficulty: choice.start === 'empty',
        level: choice.level,
        difficulty: choice.difficulty,
      }),
    );
  } catch (err) {
    failed(services, err, name);
    return null;
  }
  lib.update(() => ({ focused: id }));
  await refreshProjects(services);
  const opened = await openProject(services, { id, name: source.name }, { workspace: 'chart' });
  if (opened) services.ui.toast(ref(m.ndCreated, { level: choice.level, name }), { kind: 'success' });
  return id;
}
